import { useQuery } from '@tanstack/react-query';
import { terraProxyApiClient } from '../../clients/testerClient';

const RELATIONS_STALE_TIME_MS = 30_000;
const ASSET_TABLE_RELATED_ITEMS_LIMIT = 50;
const ASSET_TABLE_ENDPOINT_DETECTIONS_QUERY_KEY =
  'asset-table-endpoint-detections';
const ASSET_TABLE_ENDPOINT_WEBPAGES_QUERY_KEY = 'asset-table-endpoint-webpages';
const ASSET_TABLE_WEBPAGE_ENDPOINTS_QUERY_KEY = 'asset-table-webpage-endpoints';

export function useAssetTableEndpointDetections({
  endpointId,
  enabled,
}: {
  endpointId: string;
  enabled: boolean;
}) {
  const query = useQuery({
    queryKey: [ASSET_TABLE_ENDPOINT_DETECTIONS_QUERY_KEY, endpointId],
    queryFn: async () => {
      const detections =
        await terraProxyApiClient.getEndpointDetections(endpointId);

      if (!detections) {
        throw new Error('Failed to load endpoint detections');
      }

      return detections;
    },
    enabled: enabled && endpointId.length > 0,
    staleTime: RELATIONS_STALE_TIME_MS,
  });

  return {
    findings: query.data?.findings ?? [],
    signals: query.data?.signals ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useAssetTableEndpointWebpages({
  endpointId,
  enabled,
}: {
  endpointId: string;
  enabled: boolean;
}) {
  return useQuery({
    queryKey: [ASSET_TABLE_ENDPOINT_WEBPAGES_QUERY_KEY, endpointId],
    queryFn: async () => {
      const result = await terraProxyApiClient.getEndpointRelatedWebpages({
        endpointId,
        limit: ASSET_TABLE_RELATED_ITEMS_LIMIT,
      });

      if (!result) {
        throw new Error('Failed to load related webpages');
      }

      return result;
    },
    enabled: enabled && endpointId.length > 0,
    staleTime: RELATIONS_STALE_TIME_MS,
  });
}

export function useAssetTableWebpageEndpoints({
  webpageId,
  enabled,
}: {
  webpageId: string;
  enabled: boolean;
}) {
  return useQuery({
    queryKey: [ASSET_TABLE_WEBPAGE_ENDPOINTS_QUERY_KEY, webpageId],
    queryFn: async () => {
      const result = await terraProxyApiClient.getWebpageRelatedEndpoints({
        webpageId,
        limit: ASSET_TABLE_RELATED_ITEMS_LIMIT,
      });

      if (!result) {
        throw new Error('Failed to load related endpoints');
      }

      return result;
    },
    enabled: enabled && webpageId.length > 0,
    staleTime: RELATIONS_STALE_TIME_MS,
  });
}
